import React from "react";
import { useTranslation } from "react-i18next";
import { TaskIcon } from "@common/taskIcon";
import { EventIcon } from "@common/eventIcon";
import { MilestoneIcon } from "@common/milestoneIcon";
import { UnavailabilityIcon } from "../../../creationMenu/unavailabilityIcon";
import { ArrowIcon } from "@common/assets/arrowIcon";
import { SelectList } from "@common/selectList";
import classNames from "classnames";
import { Props } from "./types";
import styles from "./styles.scss";

type CreationType = "task" | "event" | "milestone" | "unavailability";

function TypeSelector({
  type,
  canCreateTask,
  canCreateEvent,
  canCreateMilestone,
  canCreateUnavailability,
  onSelect,
}: Props) {
  const { t } = useTranslation();

  const TypeIcon =
    type === "task"
      ? TaskIcon
      : type === "event"
        ? EventIcon
        : type === "milestone"
          ? MilestoneIcon
          : UnavailabilityIcon;

  const typeLabel =
    type === "task"
      ? t("listsList.quickCreation.task")
      : type === "event"
        ? t("listsList.quickCreation.event")
        : type === "milestone"
          ? t("listsList.quickCreation.milestone")
          : t("listsList.quickCreation.unavailability");

  const options: { value: CreationType; label: string; Icon: React.FC<any> }[] =
    [
      canCreateTask && type !== "task"
        ? {
            value: "task",
            label: t("listsList.quickCreation.task"),
            Icon: TaskIcon,
          }
        : null,
      canCreateEvent && type !== "event"
        ? {
            value: "event",
            label: t("listsList.quickCreation.event"),
            Icon: EventIcon,
          }
        : null,
      canCreateMilestone && type !== "milestone"
        ? {
            value: "milestone",
            label: t("listsList.quickCreation.milestone"),
            Icon: MilestoneIcon,
          }
        : null,
      canCreateUnavailability && type !== "unavailability"
        ? {
            value: "unavailability",
            label: t("listsList.quickCreation.unavailability"),
            Icon: UnavailabilityIcon,
          }
        : null,
    ].filter(Boolean) as {
      value: CreationType;
      label: string;
      Icon: React.FC<any>;
    }[];

  return (
    <SelectList<CreationType>
      listClassName={styles.typeDropdown}
      listStyle={{ right: "auto", left: 0, top: "calc(100% + 0.4rem)" }}
    >
      {({ isOpen, select }) => ({
        input: (
          <div className={styles.typeSelector}>
            <TypeIcon className={styles.typeIcon} />
            <span className={styles.typeLabel}>{typeLabel}</span>
            <ArrowIcon
              className={classNames(styles.arrow, {
                [styles.arrowOpen]: isOpen,
              })}
            />
          </div>
        ),
        list: (
          <>
            {options.map(({ value, label, Icon }) => (
              <div
                key={value}
                className={styles.typeOption}
                onClick={() => onSelect(select(value))}
              >
                <Icon className={styles.typeIcon} />
                <span>{label}</span>
              </div>
            ))}
          </>
        ),
      })}
    </SelectList>
  );
}

export { TypeSelector };
